import React, { useRef, useState, useEffect } from 'react'

const STAGGER = [0, 50, 100, 150]

const INITIAL_STYLE = {
  filter: 'blur(8px)',
  opacity: 0.4,
  transform: 'translateY(10px)',
  transition: 'none',
}

const ImpactStat = ({ number, label, index = 0 }) => {
  const ref = useRef(null)
  const rafIdRef = useRef(null)
  const isAnimatingRef = useRef(false)
  const [displayValue, setDisplayValue] = useState('0')
  const [animStyle, setAnimStyle] = useState(INITIAL_STYLE)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current)
            rafIdRef.current = null
          }
          isAnimatingRef.current = false
          setDisplayValue('0')
          setAnimStyle(INITIAL_STYLE)
          return
        }

        if (isAnimatingRef.current) return

        isAnimatingRef.current = true

        if (prefersReducedMotion) {
          setDisplayValue(number)
          setAnimStyle({ filter: 'blur(0px)', opacity: 1, transform: 'translateY(0)', transition: 'none' })
          isAnimatingRef.current = false
          return
        }

        const match = number.match(/^([\d,]+)(\+?)$/)

        if (!match) {
          setDisplayValue(number)
          setAnimStyle({ filter: 'blur(0px)', opacity: 1, transform: 'translateY(0)', transition: 'none' })
          isAnimatingRef.current = false
          return
        }

        const endValue = parseInt(match[1].replace(/,/g, ''), 10)
        const suffix = match[2] || ''
        const staggerMs = STAGGER[index] ?? 0
        const durationMs = 1400
        const startTime = performance.now() + staggerMs

        const animate = (now) => {
          const elapsed = now - startTime

          if (elapsed < 0) {
            rafIdRef.current = requestAnimationFrame(animate)
            return
          }

          const progress = Math.min(elapsed / durationMs, 1)
          const eased = 1 - Math.pow(1 - progress, 3)

          setDisplayValue(Math.round(eased * endValue).toLocaleString() + suffix)
          setAnimStyle({
            filter: `blur(${8 - eased * 8}px)`,
            opacity: 0.4 + eased * 0.6,
            transform: `translateY(${(1 - eased) * 10}px)`,
            transition: 'none',
          })

          if (progress < 1) {
            rafIdRef.current = requestAnimationFrame(animate)
          } else {
            isAnimatingRef.current = false
          }
        }

        rafIdRef.current = requestAnimationFrame(animate)
      },
      { threshold: 0.3 }
    )

    observer.observe(el)

    return () => {
      observer.disconnect()
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current)
    }
  }, [number, index])

  return (
    <div ref={ref} className="flex flex-col items-center text-center" style={animStyle}>
      <span className="text-5xl text-[#007DD6] tracking-tight">{displayValue}</span>
      <span className="text-base text-[#5B5B5B] mt-2 leading-snug">{label}</span>
    </div>
  )
}

export default ImpactStat
